import { Suspense, useEffect, useRef, useState } from "react";
import { Await } from "react-router";

import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { CaretDownIcon } from "@radix-ui/react-icons";

import { isValidationError } from "~/components/forms/validationErrorResponse";
import { VariantSelector } from "~/components/products/product-variant-selector";
import { useOptionalVariantContext } from "~/components/products/variant-selector.components";
import { Alert, AlertTitle } from "~/components/ui/alert";
import { TriButton } from "~/components/ui/tri-button";
import { QuantityStepper } from "~/hec/components/ui/quantity-stepper";
import type { VariantPriceInfo } from "~/hec/types/variant-data";
import {
  deriveAvailabilityState,
  getAvailabilityMessage,
} from "~/hec/utils/inventory-state";
import type { C_ProductInfo, Inventory, Product } from "~/lib/app.types";
import { MAX_ORDER_QUANTITY } from "~/lib/constants";
import { resolveQuantityCap } from "~/lib/quantity-cap";
import type { SerializeFrom } from "~/lib/types";
import { useFormatPrice } from "~/shop/utils/price";
import { resolvePriceFromSelectedVariant } from "~/shop/utils/price-display";

import { useBasketActions } from "../../resources/hooks/basket-hooks";
import type { CrossSellItem } from "../../resources/schemas/basket";
import { useVariantUrlSync } from "../hooks/use-variant-url-sync";
import {
  type InitialLoyaltyPoints,
  LoyaltyPointsPreview,
} from "./loyalty-points-preview";
import { ProductVariantOptions } from "./product-variant-options";

export const ProductBuyForm = ({
  product,
  productId,
  master,
  wrapOptions,
  suppressUrlSync,
  onVariantChange,
  inStockWarningLevel,
  crossSellTotal,
  crossSellItems,
  crossSellSelections,
  externalVariantContext,
  showLoyalty = false,
  acLoyaltyPoints,
  initialQuantity = 1,
  children,
}: {
  productId: string;
  product: SerializeFrom<C_ProductInfo>;
  master:
    | Promise<SerializeFrom<Product> | null>
    | SerializeFrom<Product>
    | null;
  wrapOptions?: boolean;
  /** When true, variant selection does NOT update URL search params (for quick view) */
  suppressUrlSync?: boolean;
  /** Callback when variant selection changes — used by quick view to sync images */
  onVariantChange?: (values: Record<string, string>) => void;
  /** Low-stock threshold from `inStockWarningLevel` BM site pref */
  inStockWarningLevel?: number;
  /** Cross-sell totals lifted from CrossSellSection — rendered above the qty+ATC row */
  crossSellTotal?: number;
  crossSellItems?: number;
  /** Checked cross-sell items to add alongside the main product on ATC click */
  crossSellSelections?: CrossSellItem[];
  /** When true, parent owns the VariantSelector.Selector context and the
   *  variant-options UI. This component then renders only the alerts/total/
   *  qty/ATC block, expecting to be placed inside a parent Selector. */
  externalVariantContext?: boolean;
  /** Render the Annex Cloud loyalty preview above qty+ATC. PDP only (not quick view). */
  showLoyalty?: boolean;
  /** Deferred server-rendered initial loyalty points from the PDP loader. */
  acLoyaltyPoints?: Promise<InitialLoyaltyPoints>;
  /** Seed for the quantity stepper — the originating tile's quantity
   *  (SFCC-11825). Clamped because tile steppers allow more than
   *  MAX_ORDER_QUANTITY. */
  initialQuantity?: number;
  children?: React.ReactNode;
}) => {
  const [variantError, setVariantError] = useState<boolean>(false);
  const [productQuantity, setProductQuantity] = useState<number>(
    Math.min(initialQuantity, MAX_ORDER_QUANTITY),
  );
  const basket = useBasketActions();
  const { defaultValue, onChange } = useVariantUrlSync({
    product,
    suppressUrlSync,
    onVariantChange,
  });
  // Per-variant inventory comes from the lifted `<VariantSelector>`
  // Provider in product-details.tsx. When standalone (quick view wraps its
  // own internal Selector below — the hook runs above that wrap), ctx is
  // null and we gracefully fall back to master.inventory.
  const variantCtx = useOptionalVariantContext();
  const resolvedVariantData = variantCtx?.resolvedVariantData ?? null;
  const isLoading = basket.fetcher.state !== "idle";
  const formatPrice = useFormatPrice();
  // SFCC-11602 — track the max-available qty captured at submit-time so the
  // reset effect below can cap the user's quantity back into a valid range
  // when SCAPI rejects the ATC for `product-item-quantity-not-available`. The
  // current variant's `inventory.ats` lives inside the SelectedVariant render
  // prop scope, so we mirror it into a component-level ref each time the user
  // clicks ATC. Defaults to `MAX_ORDER_QUANTITY` if inventory data isn't
  // resolved yet (graceful degradation — at worst we cap to the per-product
  // order limit, not below it).
  const lastSubmittedMaxAvailRef = useRef<number>(MAX_ORDER_QUANTITY);
  // SFCC-11602 — drive the `TriButton`'s success morph explicitly. Without
  // this, the button transitions to "✓ Done" on any fetcher settle (success
  // OR error), leaving the user stuck after an inventory-validation failure.
  // Three states: `undefined` (no settle yet, legacy default), `true` (settled
  // success), `false` (settled with validation error or other non-success
  // payload). The explicit `boolean | undefined` typing makes the three-way
  // intent legible to TriButton's state machine.
  const lastSucceeded: boolean | undefined = !basket.fetcher.data
    ? undefined
    : "successfullyAdded" in basket.fetcher.data &&
      basket.fetcher.data.successfullyAdded === true;
  // SFCC-11602 — when ATC settles with a validation error (e.g.
  // `product-item-quantity-not-available`), cap the local quantity to the
  // most-recent max-available value (captured at submit time). Floors at 1 so
  // we never land on qty=0 (which would leave the ATC button enabled with no
  // submit body — possible if the variant went OOS between page load and
  // click). When stock hits 0 mid-flight the broader `isOutOfStock` UI takes
  // over on the next render via the variant inventory.
  useEffect(() => {
    const data = basket.fetcher.data;
    if (!data || basket.fetcher.state !== "idle") return;
    if (!isValidationError(data)) return;
    setProductQuantity(prev =>
      Math.max(1, Math.min(prev, lastSubmittedMaxAvailRef.current)),
    );
  }, [basket.fetcher.data, basket.fetcher.state]);

  const productPrice = (product as unknown as { price?: number }).price ?? 0;
  // Segment/sale-aware unit price for the bundle Total — the SAME source the
  // main PDP <PriceLabel> uses (resolvePriceFromSelectedVariant → c_priceInfo).
  // The native `price` is computed PRE the AB-pricebook modifyGETResponse hook
  // (segment-blind / default book), so summing it showed the default (e.g.
  // $20.00) under a sale or AB segment while the displayed price was the sale
  // (e.g. $16.99). Falls back to the raw render-prop numbers, then native price,
  // if nothing resolves. (SFCC-11360 / SFCC-11034)
  const resolveUnitPrice = (
    currentPrice?: number,
    salePrice?: number,
    selectedPriceInfo?: VariantPriceInfo | null,
  ): number =>
    resolvePriceFromSelectedVariant(
      { currentPrice, salePrice },
      product as unknown as Record<string, unknown>,
      selectedPriceInfo,
    )?.price ??
    salePrice ??
    currentPrice ??
    productPrice;
  const grandItems = productQuantity + (crossSellItems ?? 0);

  const innerContent = (
    <>
      {!externalVariantContext && children}
      <div>
        {!externalVariantContext && (
          <ProductVariantOptions
            product={product}
            master={master}
            wrapOptions={wrapOptions}
          />
        )}

        {variantError && (
          <Alert
            className="mt-3"
            variant="error"
            data-testid="pdp-variant-error"
          >
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Select all product attributes</AlertTitle>
          </Alert>
        )}

        <div className="flex w-full flex-col justify-between sm:flex-col">
          <Suspense
            fallback={
              <>
                {crossSellItems != null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">
                      Total:{" "}
                      {formatPrice(
                        resolveUnitPrice() * productQuantity +
                          (crossSellTotal ?? 0),
                      )}
                    </span>
                    <span>Items: {grandItems}</span>
                  </div>
                )}
                <div className="mt-5 flex w-full flex-1-0-0 flex-row gap-4 self-stretch">
                  <div className="flex h-7 w-[50px] flex-row items-center justify-center rounded-md border border-neutralalpha-6 bg-neutralalpha-4 px-3 py-2 text-neutralalpha-8">
                    1<CaretDownIcon height={24} />
                  </div>
                  <TriButton
                    type="submit"
                    variant="cta"
                    disabled={true}
                    className="h-[54px] rounded-[.5rem] text-lg font-medium leading-tight"
                    name="action"
                    value="add"
                    data-testid="product-add-to-bag-button"
                  >
                    Add to Cart
                  </TriButton>
                </div>
              </>
            }
          >
            <Await resolve={master}>
              {master => {
                return (
                  <VariantSelector.SelectedVariant>
                    {({
                      salePrice,
                      currentPrice,
                      variant,
                      selectedPriceInfo,
                    }) => {
                      const getOutOfStockStatus = () => {
                        if (variant) {
                          return !variant.orderable;
                        }

                        if (product?.inventory) {
                          return !product.inventory.orderable;
                        }

                        return !product?.available;
                      };

                      const isOutOfStock = getOutOfStockStatus();
                      const unitPrice = resolveUnitPrice(
                        currentPrice,
                        salePrice,
                        selectedPriceInfo,
                      );
                      const grandTotal =
                        unitPrice * productQuantity + (crossSellTotal ?? 0);
                      // SFCC-11602 — current variant inventory (falls back to
                      // master when the variant fan-out is still resolving or
                      // when the variant has no own inventory block). Drives
                      // (a) the stepper's `max` so the user can't enter more
                      // than is stocked, and (b) the qty cap captured on ATC
                      // click — used by the post-error reset effect above.
                      const variantInventory =
                        variant && resolvedVariantData
                          ? (resolvedVariantData[variant.productId]
                              ?.inventory as Inventory | undefined)
                          : undefined;
                      const currentInventory =
                        variantInventory ??
                        (product.inventory as Inventory | undefined);
                      // Floored at 1 so the stepper's `max` never collapses to
                      // 0 if the variant goes OOS mid-render — the broader
                      // `isOutOfStock` gate disables ATC at that point, but
                      // keeping the stepper operable at qty=1 avoids a
                      // confusing collapsed-stepper state.
                      const currentMaxAvail = resolveQuantityCap(
                        currentInventory?.ats,
                      );
                      return (
                        <>
                          {crossSellItems != null && (
                            <div className="flex items-center justify-between font-grotesque">
                              <span>
                                <span className="font-grotesque text-sm font-light">
                                  Total:
                                </span>
                                <span className="font-grotesque text-sm font-medium">
                                  {" " + formatPrice(grandTotal)}
                                </span>
                              </span>

                              <span>
                                <span className="font-grotesque text-sm font-light">
                                  Items:{}
                                </span>
                                <span className="font-grotesque text-sm font-medium">
                                  {" " + grandItems}
                                </span>
                              </span>
                            </div>
                          )}
                          {/* Annex Cloud loyalty preview — above ATC, after
                              Total/Items (SFRA placement). PDP only. SFCC-11016. */}
                          {showLoyalty && acLoyaltyPoints && (
                            <Suspense fallback={null}>
                              <Await
                                resolve={acLoyaltyPoints}
                                errorElement={null}
                              >
                                {initial => (
                                  <LoyaltyPointsPreview
                                    productId={variant?.productId ?? product.id}
                                    unitPrice={unitPrice}
                                    initial={initial}
                                  />
                                )}
                              </Await>
                            </Suspense>
                          )}
                          <div className="mt-5 flex w-full flex-1-0-0 flex-row gap-4 self-stretch">
                            <div className="flex">
                              <QuantityStepper
                                quantity={productQuantity}
                                onChange={setProductQuantity}
                                max={currentMaxAvail}
                                mode="standard"
                                testIdPrefix="pdp-quantity"
                              />
                            </div>
                            <div className="flex min-w-0 flex-1">
                              <TriButton
                                type="submit"
                                variant="cta"
                                disabled={isLoading || isOutOfStock}
                                className="h-[54px] w-full animate-popup-strong rounded-[.5rem] text-lg font-medium leading-tight md:w-[150px]"
                                isLoading={isLoading}
                                succeeded={lastSucceeded}
                                name="action"
                                value="add"
                                data-testid="product-add-to-bag-button"
                                onClick={() => {
                                  /* We want to keep the button available but show error if not all attributes are selected */
                                  /* A master/variant-bearing product has no orderable SKU until a
                                     variant is resolved — never fall through to product.id (the
                                     master), which SCAPI rejects on the basket. (SFCC-11359) */
                                  const requiresVariant =
                                    Boolean(product.type?.master) ||
                                    Boolean(
                                      product.variationAttributes?.length,
                                    ) ||
                                    Boolean(product.variants?.length);
                                  if (!variant && requiresVariant) {
                                    setVariantError(true);
                                    return;
                                  }
                                  /* assume all attributes selected from here */
                                  const mainProductId = variant
                                    ? variant.productId
                                    : product.id;
                                  // SFCC-11602 — capture the at-submit max so
                                  // the post-error reset effect knows what to
                                  // cap to (the variant inventory at the time
                                  // of the click; safer than re-deriving in
                                  // the effect, since the user could change
                                  // the variant between submit and settle).
                                  lastSubmittedMaxAvailRef.current =
                                    currentMaxAvail;
                                  if (
                                    crossSellSelections &&
                                    crossSellSelections.length > 0
                                  ) {
                                    basket.addMany(
                                      [
                                        {
                                          productId: mainProductId,
                                          quantity: productQuantity,
                                        },
                                        ...crossSellSelections,
                                      ],
                                      {
                                        // QV + hybrid PDP both set
                                        // suppressUrlSync → "grid"; standard PDP
                                        // → "main". (SFRA's distinct "hybrid"
                                        // value is a later refinement.)
                                        clickSource: suppressUrlSync
                                          ? "grid"
                                          : "main",
                                        quickView: Boolean(suppressUrlSync),
                                      },
                                    );
                                  } else {
                                    // `mainProductId` is the resolved variant
                                    // SKU — the orderable item — so the selected
                                    // variation values aren't needed (and SCAPI
                                    // rejects them on the basket item). Send the
                                    // SKU only. (SFCC-11255 / SFCC-11094 cleanup)
                                    basket.add(
                                      { productId: mainProductId },
                                      productQuantity,
                                      {
                                        clickSource: suppressUrlSync
                                          ? "grid"
                                          : "main",
                                        quickView: Boolean(suppressUrlSync),
                                      },
                                    );
                                  }
                                  setVariantError(false);
                                }}
                              >
                                {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                              </TriButton>
                            </div>
                          </div>
                          {/* Availability message — below the qty + ATC row on
                              desktop and mobile (devhec/SFRA parity); never part
                              of the sticky bar. Mirrors SFRA availability.js:
                              low stock -> "Only N units left", otherwise in stock
                              -> "In Stock" (label.instock). Pre/back-order copy
                              comes from getAvailabilityMessage. */}
                          {(() => {
                            const availabilityText = (
                              inventory: Inventory | undefined,
                            ): string | null => {
                              const availability =
                                deriveAvailabilityState(inventory);
                              const availabilityMessage =
                                getAvailabilityMessage(availability);
                              if (availabilityMessage)
                                return availabilityMessage;
                              if (isOutOfStock) return null;
                              const ats = inventory?.ats;
                              // Low-stock warning (SFRA: ATS < inStockWarningLevel)
                              if (
                                ats &&
                                inStockWarningLevel &&
                                ats < inStockWarningLevel
                              ) {
                                return ats === 1
                                  ? "Only 1 unit left"
                                  : `Only ${ats} units left`;
                              }
                              // Fully in stock (SFRA: label.instock)
                              if (availability.kind === "inStock")
                                return "In Stock";
                              return null;
                            };

                            // SFCC-11602 — reuse the already-derived
                            // `currentInventory` (variant ?? master) from the
                            // SelectedVariant render-prop scope above instead
                            // of re-deriving it here.
                            const text = availabilityText(currentInventory);
                            return text ? (
                              <p
                                className="mt-4 font-grotesque text-sm font-light"
                                data-testid="pdp-availability"
                              >
                                {text}
                              </p>
                            ) : null;
                          })()}
                        </>
                      );
                    }}
                  </VariantSelector.SelectedVariant>
                );
              }}
            </Await>
          </Suspense>
        </div>
      </div>
    </>
  );

  return (
    <div className="product-buy-form font-grotesque text-blue-04">
      {externalVariantContext ? (
        innerContent
      ) : (
        <VariantSelector.Selector
          product={product}
          master={master}
          productId={productId}
          defaultValue={defaultValue}
          onChange={onChange}
        >
          {innerContent}
        </VariantSelector.Selector>
      )}
    </div>
  );
};
